/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Envuelve pantallas del Shell/Tabs. El título vive en el header operativo;
 * `hideBodyTitle` (default en MainTabs y Shell) evita H1 duplicado en el cuerpo.
 */
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { useEffectiveBusinessCapabilities } from '../session/hooks/useEffectiveBusinessCapabilities';
import { ScreenPageTitle } from './ScreenPageTitle';
import { resolveScreenPageTitle } from './resolveScreenPageTitle';

const COMPACT_ROUTES = new Set(['POS']);

type WithScreenPageTitleOptions = {
  /** Título ya está en el header operativo (MainTabs + ShellStack). */
  hideBodyTitle?: boolean;
};

export function withScreenPageTitle<P extends object>(
  Screen: React.ComponentType<P>,
  options?: WithScreenPageTitleOptions,
): React.ComponentType<P> {
  const hideBodyTitle = options?.hideBodyTitle === true;

  function Wrapped(props: P) {
    const route = useRoute();
    const caps = useEffectiveBusinessCapabilities();
    const title = resolveScreenPageTitle(route.name, caps);
    const compact = COMPACT_ROUTES.has(route.name);

    if (!Screen) {
      return <View style={styles.host} />;
    }

    return (
      <View style={styles.host}>
        {hideBodyTitle ? null : <ScreenPageTitle title={title} compact={compact} />}
        <View style={styles.body}>
          <Screen {...props} />
        </View>
      </View>
    );
  }

  // Guard: circular/HMR graphs can pass undefined Screen briefly (throws on .displayName).
  const screenName = Screen?.displayName || Screen?.name || 'Screen';
  Wrapped.displayName = `WithPageTitle(${screenName})`;
  return Wrapped;
}

const styles = StyleSheet.create({
  host: {
    flex: 1,
    minHeight: 0,
  },
  body: {
    flex: 1,
    minHeight: 0,
  },
});
